import {
  BriefcaseIcon,
  CheckIcon,
  EyeIcon,
  Loader2Icon,
  LockIcon,
  MessagesSquareIcon,
  ShieldCheckIcon,
  TriangleAlertIcon,
  UsersIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { AuthError } from '@/modules/auth/components/AuthError';
import { AuthLayout } from '@/modules/layouts/components/authLayout/AuthLayout';
import { DocumentMetadata } from '@/modules/layouts/components/DocumentMetadata';
import { canAddCandidates } from '@/modules/mcp/access';
import { findClientBrand } from '@/modules/mcp/clientBrands';
import {
  ClientTile,
  FinlayTile,
} from '@/modules/mcp/components/clientTile/ClientTile';
import { Button } from '@/modules/ui/components/button/Button';
import type { CurrentUserApi } from '@/modules/users/types';
import type { components } from '@/types/apiSchema';

type OAuthConsentResponse = components['schemas']['OAuthConsentResponse'];

/**
 * What a connection reaches, by kind of data, each at the level its scopes
 * grant. New MCP tools fall under one of these, so the list only grows when a
 * new kind of data does; write tools change an area's level, not the list.
 * `finlay:write` only adds candidates, so only Candidates shows it.
 */
const accessAreas = [
  {
    icon: BriefcaseIcon,
    title: 'Jobs',
    description: 'Details, pipeline stages and status.',
  },
  {
    icon: UsersIcon,
    title: 'Candidates',
    description: 'Profiles, briefs and where they are in the pipeline.',
    writeLabel: 'View and add',
  },
  {
    icon: MessagesSquareIcon,
    title: 'Conversations',
    description: 'Emails and messages with candidates.',
  },
];

interface OAuthConsentCardProps {
  /** Absent while the request loads. */
  consent?: OAuthConsentResponse;
  /** The signed-in account the client would act as. */
  user?: CurrentUserApi;
  /** The workspace the connection reads from. */
  workspaceName?: string;
  /** The request is missing, expired, or belongs to another account. */
  invalid: boolean;
  /** The answer being sent: true to connect, false to cancel. */
  decision?: boolean;
  decisionFailed: boolean;
  onDecide: (approve: boolean) => void;
}

/** What `/oauth/consent` shows while an MCP client asks to connect. */
export const OAuthConsentCard = ({
  consent,
  user,
  workspaceName,
  invalid,
  decision,
  decisionFailed,
  onDecide,
}: OAuthConsentCardProps) => {
  if (invalid) {
    return (
      <AuthLayout
        heading="This link has expired"
        subheading="Connection requests are valid for 10 minutes and only for the account that started them. Go back to the app you were connecting and try again."
      >
        <DocumentMetadata title="Connection request expired" />
      </AuthLayout>
    );
  }

  if (!consent) {
    return (
      <AuthLayout
        heading="Connect to Finlay"
        subheading="Loading the connection request."
      >
        <DocumentMetadata title="Connect to Finlay" />
        <div role="status" className="flex justify-center py-2">
          <Loader2Icon
            className="text-muted-foreground size-5 animate-spin"
            aria-hidden="true"
          />
          <span className="sr-only">Loading the connection request</span>
        </div>
      </AuthLayout>
    );
  }

  const { clientName, redirectHost, scopes } = consent;
  // The client picks its own name; the redirect host is who actually receives
  // the access. Only a known brand's host earns the name in the copy.
  const isVerified = findClientBrand(consent) !== undefined;
  const appName = isVerified ? clientName : 'This app';
  const isDeciding = decision !== undefined;
  const canEdit = canAddCandidates(scopes);
  const userName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

  return (
    <div className="portal text-foreground flex min-h-svh flex-col items-center bg-[#fafafa] bg-[radial-gradient(ellipse_70%_45%_at_50%_0%,#ece8ffcc,transparent)] px-4 py-10 md:py-16">
      <DocumentMetadata
        title={isVerified ? `Connect ${clientName}` : 'Connect an app'}
      />

      <div className="flex items-center gap-3 md:gap-3.5">
        <ClientTile client={consent} />
        <div className="flex items-center gap-1.5" aria-hidden="true">
          <ConnectorDots />
          <span className="bg-background flex size-7 items-center justify-center rounded-full border">
            <CheckIcon className="text-primary size-3.5" />
          </span>
          <ConnectorDots />
        </div>
        <FinlayTile />
      </div>

      <div className="mt-7 max-w-[520px] text-center">
        <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.02em] text-balance md:text-[28px] md:leading-9">
          {isVerified
            ? `${clientName} wants to connect to Finlay`
            : `An app at ${redirectHost} wants to connect to Finlay`}
        </h1>
        <p className="text-muted-foreground mt-2.5 text-[15px] leading-6 text-pretty">
          Ask {isVerified ? clientName : 'it'} about your jobs and candidates
          right from your chats. Only allow this if you started the connection.
        </p>
      </div>

      <div className="bg-background mt-7 w-full max-w-[480px] overflow-hidden rounded-2xl border shadow-[0_12px_32px_rgba(24,24,28,0.06)]">
        {user && (
          <div className="min-w-0 border-b px-5 py-3.5 md:px-6">
            <p className="truncate font-semibold">
              {workspaceName ?? userName}
            </p>
            <p className="text-muted-foreground truncate text-[13px]">
              Signed in as {user.email}
            </p>
          </div>
        )}

        <div className="space-y-4 px-5 pt-5 pb-6 md:px-6">
          {!isVerified && (
            <div className="flex items-start gap-2.5 rounded-[10px] border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] leading-[1.45] text-amber-900">
              <TriangleAlertIcon
                className="mt-0.5 size-4 shrink-0 text-amber-600"
                aria-hidden="true"
              />
              <p>
                <span className="font-semibold">
                  Finlay can’t verify this app.
                </span>{' '}
                It calls itself “{clientName}”, but access goes to{' '}
                <span className="font-semibold break-words">
                  {redirectHost}
                </span>
                . Allow access only if you added this connector yourself and you
                trust that site.
              </p>
            </div>
          )}

          <p className="text-[13px] font-semibold">{appName} can access</p>
          <ul className="space-y-3.5">
            {accessAreas.map(
              ({ icon: Icon, title, description, writeLabel }) => (
                <li key={title} className="flex items-center gap-3">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f1eeff]">
                    <Icon className="text-primary size-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{title}</p>
                    <p className="text-muted-foreground text-[13px] leading-[1.4]">
                      {description}
                    </p>
                  </div>
                  <span className="bg-muted text-muted-foreground inline-flex h-[22px] shrink-0 items-center rounded-full px-2 text-xs font-medium">
                    {canEdit && writeLabel ? writeLabel : 'View'}
                  </span>
                </li>
              ),
            )}
          </ul>

          <p className="bg-muted text-muted-foreground flex items-start gap-2.5 rounded-[10px] px-3 py-2.5 text-[13px] leading-[1.45]">
            {canEdit ? (
              <ShieldCheckIcon
                className="mt-0.5 size-4 shrink-0"
                aria-hidden="true"
              />
            ) : (
              <EyeIcon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            )}
            {canEdit
              ? `${appName} can add candidates to your jobs as you, which uses your sourced-candidate allowance. It can’t change anything else, and sees only what you can see.`
              : `View only. ${appName} can’t change anything in Finlay or message candidates, and sees only what you can see.`}
          </p>

          {decisionFailed && (
            <AuthError
              message={`We couldn’t send your answer. The request may have expired: go back to ${clientName} and connect again.`}
            />
          )}
        </div>

        <div className="border-t bg-[#fcfcfd] px-5 py-4 md:px-6">
          <p className="text-muted-foreground mb-3 flex items-center justify-center gap-1.5 text-center text-[13px]">
            <LockIcon className="size-3 shrink-0" aria-hidden="true" />
            <span className="min-w-0">
              After you allow, you’ll return to{' '}
              <span className="text-foreground font-semibold break-words">
                {redirectHost}
              </span>
            </span>
          </p>
          <div className="flex flex-col-reverse gap-3 sm:flex-row">
            <Button
              variant="outline"
              className="h-11 flex-1 rounded-[10px] font-medium"
              disabled={isDeciding}
              loading={decision === false}
              onClick={() => onDecide(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="h-11 flex-1 rounded-[10px] font-semibold"
              disabled={isDeciding}
              loading={decision === true}
              onClick={() => onDecide(true)}
            >
              Allow access
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-7 text-center text-[13px] text-[#8f8f9b]">
        <p>
          {appName} stays connected until you disconnect it in{' '}
          <Link
            to="/hm/integrations"
            className="text-muted-foreground hover:text-foreground font-medium underline underline-offset-2"
          >
            Integrations
          </Link>
          .
        </p>
      </div>
    </div>
  );
};

const ConnectorDots = () => (
  <span className="flex gap-1.5">
    {[0, 1, 2].map((dot) => (
      <span key={dot} className="size-1 rounded-full bg-[#c9c0ff]" />
    ))}
  </span>
);
